import type { FundRatingChip } from "../lib/types";

/**
 * Per-fund rating chip rendered inline beneath an Owl answer.
 *
 * Visual proof of NYVO's rating – tier badge (color-coded) + name +
 * 1Y / 3Y CAGR + risk band. Compact enough to stack 3-5 in a chat
 * bubble.
 *
 * 2026-05-26 chat polish: the composite *score* no longer appears as
 * a number anywhere. The tier itself is the verdict; the actual
 * return + risk numbers are what a customer actually needs to know.
 *
 * Compliance: descriptive only. Tier badges show what NYVO published,
 * never a buy/sell directive. The disclaimer copy lives on the parent
 * (one line under all the chips) so we don't repeat it per chip.
 */
export function FundChip({ chip }: { chip: FundRatingChip }) {
  const oneY = chip.trail_1y_pct ?? null;
  const threeY = chip.trail_3y_cagr_pct ?? null;
  // 2026-05-29 — NR (debt / liquid) funds carry no 1Y/3Y returns, so
  // "1y – · 3y CAGR –" reads as broken. For those, swap the returns
  // metrics for the facts that actually matter on a debt fund: scheme
  // AUM + expense ratio. Falls back to SEBI risk only when neither is
  // available.
  const hasReturns = oneY !== null || threeY !== null;
  const aum = chip.aum_inr_cr ?? null;
  const ter = chip.expense_ratio_pct ?? null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2.5 transition hover:border-nyvo-300">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {/* 2026-05-27 (v9.2) – removed `truncate` so similar-AMC funds
              don't get visually collapsed (e.g. "Bandhan Large & Mid
              Cap" was getting chopped to "Bandhan Large…" making it
              indistinguishable from "Bandhan Large Cap"). Allow wrap;
              fund names are short enough to fit in 1-2 lines. */}
          <div className="text-xs font-medium leading-tight text-nyvo-900">
            {shortName(chip.fund_name)}
          </div>
          {chip.amc && (
            <div className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-400">
              {chip.amc}
              {chip.category ? ` · ${chip.category}` : ""}
            </div>
          )}
        </div>
        <TierBadge tier={chip.tier} />
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px]">
        {hasReturns ? (
          <>
            <Metric label="1y" value={fmtPct(oneY)} signed />
            <Metric label="3y CAGR" value={fmtPct(threeY)} signed />
          </>
        ) : (
          <>
            {/* NR/debt fund: show AUM + TER instead of empty returns. */}
            {aum !== null && <Metric label="AUM" value={fmtAum(aum)} />}
            {ter !== null && <Metric label="TER" value={`${ter.toFixed(2)}%`} />}
          </>
        )}
        {/* 2026-05-28 (reviewer finding P2.2) — was "risk", but the
            field is the SEBI Riskometer band ("Very High", "Moderate"
            etc.), NOT realised volatility. AI prose still uses
            "volatility" with std-dev cutoffs — same word, two sources,
            looks contradictory. Labelling the chip "SEBI risk" makes
            the source explicit. */}
        <Metric label="SEBI risk" value={chip.risk_band ?? "–"} />
      </div>
      {/* 2026-07-13 (question-bank v4, Recipe A) — the comparison + the evidence.
          The 3Y gap vs the category median turns a raw return into a comparison;
          "why it appears" is one verified strength, "what to watch" one honest
          trade-off. All server-derived; shown only when present (never guessed). */}
      {typeof chip.delta_vs_category_3y_pp === "number" && (
        <div className="mt-1.5 text-[10px] font-mono tabular-nums text-slate-500">
          <span className={chip.delta_vs_category_3y_pp >= 0 ? "text-emerald-700" : "text-red-700"}>
            {chip.delta_vs_category_3y_pp >= 0 ? "+" : ""}
            {chip.delta_vs_category_3y_pp.toFixed(1)} pp
          </span>{" "}
          vs category (3Y)
        </div>
      )}
      {chip.why_it_appears && (
        <div className="mt-1.5 flex items-start gap-1 text-[10px] leading-tight text-emerald-700">
          <span aria-hidden>✓</span>
          <span className="text-slate-600">{chip.why_it_appears}</span>
        </div>
      )}
      {chip.what_to_watch && (
        <div className="mt-0.5 flex items-start gap-1 text-[10px] leading-tight text-amber-700">
          <span aria-hidden>⚠</span>
          <span className="text-slate-600">{chip.what_to_watch}</span>
        </div>
      )}
      {/* 2026-06-08 (B17) — staleness signal: NYVO ratings refresh
          quarterly, so a published tier can be weeks old. Show how fresh
          the rating is so the customer knows the "as of". */}
      {ratedAgo(chip.rating_date) && (
        <div className="mt-1 text-[9px] uppercase tracking-wider text-slate-400">
          {ratedAgo(chip.rating_date)}
        </div>
      )}
    </div>
  );
}

/**
 * Compact secondary chip for peer funds rendered under the primary
 * answer. One row per peer – name + tier + 1Y return. Smaller visual
 * weight so the queried fund stays prominent.
 *
 * Chosen layout (Option A from the design discussion): the queried
 * fund's full chip sits above, then a small "Other Healthy in this
 * category" header, then these row-style peers underneath.
 */
export function PeerChip({ chip }: { chip: FundRatingChip }) {
  const oneY = chip.trail_1y_pct ?? null;
  const threeY = chip.trail_3y_cagr_pct ?? null;
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-slate-100 bg-slate-50/50 px-2.5 py-1.5">
      <div className="min-w-0 flex-1">
        {/* 2026-05-27 (v9.2) – `truncate` dropped (see FundChip note). */}
        <div className="text-[11px] font-medium leading-tight text-nyvo-900">
          {shortName(chip.fund_name)}
        </div>
        {chip.amc && (
          <div className="text-[9px] uppercase tracking-wider text-slate-400">
            {chip.amc}
            {chip.category ? ` · ${chip.category}` : ""}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2 text-[10px] font-mono tabular-nums">
        {oneY !== null && (
          <span className={tone(oneY)}>1Y {fmtPct(oneY)}</span>
        )}
        {threeY !== null && (
          <span className={tone(threeY)}>3Y {fmtPct(threeY)}</span>
        )}
        <TierBadge tier={chip.tier} small />
      </div>
    </div>
  );
}

/* ────────── parts ────────── */

// 2026-05-27 (tier copy update) – verbatim per user. Outcome-framed:
// each tier describes what the customer SHOULD think about doing,
// not just an adjective. Lives on the badge's `title` attribute so
// hover/tap shows the explainer everywhere a TierBadge renders.
// Exported so the About panel can render the same canonical copy
// in its "What the tier badges mean" sub-section without drift.
export const TIER_TOOLTIPS: Record<string, string> = {
  Healthy:
    "Healthy – This fund has better performance vs its category peers, "
    + "with risk well under control. Suitable for SIPs and core long-term "
    + "holding.",
  Stable:
    "Stable – A dependable performer that's tracking its category, just "
    + "not leading it. Fine to continue holding, but a Healthy fund may "
    + "be a better choice for new SIPs.",
  Watch:
    "Watch – Recent performance or risk metrics have deteriorated. Stay "
    + "invested, but wait for the next refresh before adding more.",
  Critical:
    "Critical – The fund is meaningfully behind its peers or carrying "
    + "more risk than expected. Consider switching.",
};

function TierBadge({
  tier, small = false,
}: { tier: string; small?: boolean }) {
  const cls = tierClass(tier);
  const sizing = small
    ? "px-1.5 py-0 text-[8px]"
    : "px-1.5 py-0.5 text-[9px]";
  const tooltip = TIER_TOOLTIPS[tier];
  return (
    <span
      className={`shrink-0 rounded-full font-bold uppercase tracking-wider ${sizing} ${cls}`}
      title={tooltip}
      aria-label={tooltip ? `${tier} – ${tooltip}` : tier}
    >
      {tier}
    </span>
  );
}

function Metric({
  label,
  value,
  signed = false,
}: {
  label: string;
  value: string;
  signed?: boolean;
}) {
  const toneCls =
    signed && value.startsWith("+")
      ? "text-up"
      : signed && value.startsWith("-")
      ? "text-down"
      : "text-nyvo-900";
  return (
    <div className="flex flex-col items-start">
      <span className="text-[9px] uppercase tracking-wider text-slate-400">
        {label}
      </span>
      <span className={`font-mono font-semibold ${toneCls}`}>{value}</span>
    </div>
  );
}

/* ────────── format helpers ────────── */

// 2026-06-08 (B17) — "Rated N days ago" freshness label. Under ~6 weeks
// shows a relative age; older than that shows the absolute date so it reads
// as a clear "as of" (ratings refresh quarterly).
function ratedAgo(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.round((Date.now() - d.getTime()) / 86_400_000);
  if (days < 0) return null;
  if (days === 0) return "Rated today";
  if (days === 1) return "Rated 1 day ago";
  if (days < 45) return `Rated ${days} days ago`;
  return `Rated ${d.toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  })}`;
}

function tierClass(tier: string): string {
  switch (tier) {
    case "Healthy":  return "bg-emerald-100 text-emerald-800";
    case "Stable":   return "bg-sky-100 text-sky-800";
    case "Watch":    return "bg-amber-100 text-amber-800";
    case "Critical": return "bg-red-100 text-red-800";
    default:         return "bg-slate-100 text-slate-700";  // NR / unknown
  }
}

function fmtPct(n: number | null): string {
  if (n === null || Number.isNaN(n)) return "–";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
}

// 2026-05-29 — compact AUM for the chip. Input is INR crore. Above
// 1,00,000 cr render in lakh-crore ("₹1.2L cr"); otherwise round to
// whole crore with Indian-style grouping ("₹72,873 cr").
function fmtAum(cr: number | null): string {
  if (cr === null || Number.isNaN(cr)) return "–";
  if (cr >= 100000) return `₹${(cr / 100000).toFixed(1)}L cr`;
  return `₹${Math.round(cr).toLocaleString("en-IN")} cr`;
}

function tone(v: number): string {
  if (v > 0) return "text-emerald-700";
  if (v < 0) return "text-red-700";
  return "text-slate-600";
}

/**
 * Strip the common '- Direct Plan - Growth' suffix so chip names fit.
 * "HDFC Flexi Cap Fund - Growth Option - Direct Plan" → "HDFC Flexi Cap Fund"
 */
function shortName(name: string): string {
  return name
    .replace(/\s*[-–]?\s*Direct\s+Plan.*$/i, "")
    .replace(/\s*[-–]?\s*Growth(\s+Option|\s+Plan)?.*$/i, "")
    .replace(/\s*-\s*$/g, "")
    .trim();
}
